import { PLANS } from './constants';
import i18n from './i18n';

export type BillingCycle = 'monthly' | 'yearly';
export type PlanKey = 'premium' | 'family';

export type CheckoutPlan = 'monthly' | 'yearly' | 'family-monthly' | 'family-yearly';

/** Variant IDs mirror supabase/functions/_shared/lemonsqueezy.ts. */
export const CHECKOUT_TO_VARIANT: Record<CheckoutPlan, string> = {
  'monthly': '1742305',
  'yearly': '1742224',
  'family-monthly': '1742326',
  'family-yearly': '1742307',
};

export const VARIANT_TO_CHECKOUT: Record<string, CheckoutPlan> = Object.fromEntries(
  Object.entries(CHECKOUT_TO_VARIANT).map(([k, v]) => [v, k as CheckoutPlan])
) as Record<string, CheckoutPlan>;

export function checkoutPlanFromVariant(variantId: string | null | undefined): CheckoutPlan | null {
  if (!variantId) return null;
  return VARIANT_TO_CHECKOUT[String(variantId)] ?? null;
}


export interface PlanOption {
  key: PlanKey;
  name: string;
  includedUsers: number;
  /** e.g. "$79" */
  headlinePrice: string;
  /** e.g. "/year" */
  period: string;
  /** e.g. "≈ $6.58/mo, billed annually" or "Billed monthly" */
  subtext: string;
  /** e.g. "$79/year" */
  priceShort: string;
  /** e.g. "≈ $6.58/mo" - terse, for compact pickers */
  perMonthApprox: string | null;
  checkoutPlan: CheckoutPlan;
  /** Rounded percent saved on yearly vs paying monthly_anchor * 12 */
  savePercent: number;
}

function planFor(key: PlanKey) {
  return key === 'premium' ? PLANS.premium : PLANS.family;
}

export function getPlanOption(key: PlanKey, cycle: BillingCycle): PlanOption {
  const p = planFor(key);
  const annual = p.annualPrice;
  const monthly = p.monthlyAnchor;
  const perMonthFromAnnual = annual / 12;

  const isYearly = cycle === 'yearly';

  const headlinePrice = isYearly ? `$${annual}` : `$${monthly}`;
  const period = isYearly ? i18n.t('pricing:periodYear') : i18n.t('pricing:periodMonth');
  const subtext = isYearly
    ? i18n.t('pricing:subtextYearly', { amount: perMonthFromAnnual.toFixed(2) })
    : i18n.t('pricing:billedMonthly');
  const priceShort = isYearly
    ? i18n.t('pricing:priceShortYear', { price: `$${annual}` })
    : i18n.t('pricing:priceShortMonth', { price: `$${monthly}` });
  const perMonthApprox = isYearly ? i18n.t('pricing:perMonthApprox', { amount: perMonthFromAnnual.toFixed(2) }) : null;

  const checkoutPlan: CheckoutPlan =
    key === 'premium'
      ? (isYearly ? 'yearly' : 'monthly')
      : (isYearly ? 'family-yearly' : 'family-monthly');

  return {
    key,
    name: i18n.t(`pricing:planNames.${key}`),
    includedUsers: p.includedUsers,
    headlinePrice,
    period,
    subtext,
    priceShort,
    perMonthApprox,
    checkoutPlan,
    savePercent: getYearlySavingsPercent(key),
  };
}

export function getYearlySavingsPercent(key: PlanKey): number {
  const p = planFor(key);
  const monthlyTotal = p.monthlyAnchor * 12;
  if (!monthlyTotal) return 0;
  return Math.round((1 - p.annualPrice / monthlyTotal) * 100);
}

export function formatPlanPriceShort(key: PlanKey, cycle: BillingCycle): string {
  return getPlanOption(key, cycle).priceShort;
}

export type PublicPlanKey = 'free' | PlanKey;

/** Translated plan display name, including the free plan (not covered by getPlanOption). */
export function getPlanName(key: PublicPlanKey): string {
  return i18n.t(`pricing:planNames.${key}`);
}

/** Translated feature-bullet list for a plan, with family member counts interpolated. */
export function getPlanBullets(key: PublicPlanKey): string[] {
  const count = PLANS.family.includedUsers;
  const total = count + 1;
  // returnObjects skips ICU formatting, so format each bullet by its index key.
  const raw = i18n.t(`pricing:bullets.${key}`, { returnObjects: true }) as unknown;
  const length = Array.isArray(raw) ? raw.length : 0;
  return Array.from({ length }, (_, i) => i18n.t(`pricing:bullets.${key}.${i}`, { count, total }));
}
